import { cn } from "@devhop/ui";
import { useMemo } from "react";
import {
  computeLineItemsQtyByUnit,
  type LineItemQtyInput,
} from "../lib/line-items-qty-by-unit";

type DocumentLineQtyByUnitSummaryProps = {
  items: LineItemQtyInput[];
  className?: string;
  /** ສະແດງກ່ອນມີ qty (ໃຊ້ໃນຟອມສ້າງ/ແກ້) */
  alwaysShow?: boolean;
  /** ຊ່ອນຫົວຂໍ້ເມື່ອຫ่อ Card ມີ title ແລ້ວ */
  hideHeader?: boolean;
  variant?: "default" | "compact";
};

function QtyByUnitChips({
  rows,
  compact,
}: {
  rows: Array<{ unit: string; quantity: number }>;
  compact?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <span className="text-muted-foreground text-sm">
        ຍັງບໍ່ມີຈຳນວນໃນລາຍການ
      </span>
    );
  }

  return (
    <ul className={cn("flex flex-wrap gap-2", compact && "gap-1.5")}>
      {rows.map((row) => (
        <li
          key={row.unit}
          className={cn(
            "inline-flex items-center gap-2 rounded-md border bg-background tabular-nums",
            compact ? "px-2 py-0.5 text-xs" : "px-3 py-1.5 text-sm",
          )}
        >
          <span className="font-semibold">{row.quantity.toLocaleString()}</span>
          <span className="text-muted-foreground">{row.unit}</span>
        </li>
      ))}
    </ul>
  );
}

/** ສະຫຼຸບ qty ຕາມຫນ່ວຍ — ສຳລັບຜູ້ໃຊ້ເບິ່ງເທົ່ານັ້ນ, ບໍ່ລວມໃນ PDF/print */
export function DocumentLineQtyByUnitSummary({
  items,
  className,
  alwaysShow = false,
  hideHeader = false,
  variant = "default",
}: DocumentLineQtyByUnitSummaryProps) {
  const rows = useMemo(() => computeLineItemsQtyByUnit(items), [items]);

  if (rows.length === 0 && !alwaysShow) return null;

  if (variant === "compact") {
    return (
      <div
        className={cn("flex min-w-0 flex-wrap items-center gap-2 print:hidden", className)}
        data-testid="document-qty-by-unit-summary"
      >
        <span className="shrink-0 font-medium text-muted-foreground text-xs">
          ສະຫຼຸບ qty:
        </span>
        <QtyByUnitChips rows={rows} compact />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-dashed bg-muted/30 p-4 print:hidden",
        className,
      )}
      data-testid="document-qty-by-unit-summary"
    >
      {!hideHeader && (
        <>
          <p className="font-medium text-sm">ສະຫຼຸບຈຳນວນຕາມຫນ່ວຍ</p>
          <p className="mt-0.5 text-muted-foreground text-xs">
            ສຳລັບອ້າງອີງພາຍໃນ — ບໍ່ລວມໃນເອກະສານທີ່ພິມ
          </p>
        </>
      )}
      <div className={cn(!hideHeader && "mt-3")}>
        <QtyByUnitChips rows={rows} />
      </div>
    </div>
  );
}
